import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Appetite_Type_Enum,
  UpdateAppetiteMutation,
  UpdateAppetiteMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateAppetiteDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateAppetiteTRPCInput = Omit<
  UpdateAppetiteMutationVariables,
  'AppetiteType' | 'CustomAttributeData'
> & {
  AppetiteType: Appetite_Type_Enum;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateAppetite = createMutationHook({
  name: 'updateAppetite',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.appetite.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateAppetiteMutationVariables
  ): UpdateAppetiteTRPCInput => {
    const { AppetiteType, ...rest } = variables;
    if (!AppetiteType) {
      throw new Error('AppetiteType is required for appetite update');
    }

    return {
      // `OriginalTimestamp` rides along: the legacy mutation pins it in its
      // `where`, so dropping it here is exactly the V3 lock loss COI-622 is
      // closing.
      ...rest,
      AppetiteType,
      CustomAttributeData: rest.CustomAttributeData ?? null,
    };
  },
  invalidate: (trpc) => [
    trpc.frontend.appetite.register.queryKey(),
    trpc.frontend.appetite.getById.queryKey(),
    trpc.frontend.appetite.appetitesByRiskId.queryKey(),
    trpc.frontend.appetite.activeAppetitesByParentId.queryKey(),
    trpc.frontend.appetite.getAppetitesGroupedByImpact.queryKey(),
  ],
  mapResponse: (): UpdateAppetiteMutation => ({
    update_appetite: {
      __typename: 'appetite_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateAppetiteDocument,
  graphqlVariables: (variables: UpdateAppetiteMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['appetite'],
});
